import { AstraBadge, AstraButton, AstraDialog, AstraInputWrapper, AstraMultiSelect, AstraParagraph, AstraSelect, AstraTextField, astraTwMerge } from '~/astra'
import { mapAssignmentToLabel, PractitionerAssignment, PractitionerStatusDefinition } from '~/utils/surgeryActivities'

import { createFormFieldMetaProps } from '../../Form/utils'
import { ColorPicker } from './ColorPicker'
import { suggestShortcut, timeOfDayOptions, toKeyboardShortcut } from './formUtils'
import { KeyboardShortcutField } from './KeyboardShortcutField'
import { StatusPreview } from './StatusPreview'
import { useStatusDefinitionForm } from './useStatusDefinitionForm'

type Props = {
    open: boolean
    setOpen: (open: boolean) => void
    assignmentName: PractitionerAssignment

    // Simple logic. If status definition is empty then we create a new status
    // If there is any data then we update the existing status
    statusDefinition?: PractitionerStatusDefinition
}

export const StatusDefinitionForm = ({ assignmentName, statusDefinition, open, setOpen }: Props) => {
    const { form, colorPalette, existingShortcuts, departmentOptions, applyShortcutToForm } = useStatusDefinitionForm({
        setOpenDialog: setOpen,
        assignmentName,
        statusDefinition,
    })

    return (
        <AstraDialog
            title="Ny aktivitet"
            className="w-[542px]"
            afterTitle={<AstraBadge text={mapAssignmentToLabel(assignmentName)} />}
            isOpen={open}
            onCloseDialog={() => {
                form.reset()
                setOpen(false)
            }}
            footer={
                <form.Subscribe selector={state => ({ isTouched: state.isTouched, isSubmitting: state.isSubmitting })}>
                    {({ isTouched, isSubmitting }) => (
                        <div className="flex justify-end gap-x-2">
                            {isTouched && !isSubmitting && <AstraButton variant="tertiary" label="Tilbakestill" onClick={() => form.reset()} />}
                            <AstraButton label="Lagre aktivitet" onClick={form.handleSubmit} isLoading={isSubmitting} disabled={!isTouched || isSubmitting} />
                        </div>
                    )}
                </form.Subscribe>
            }
        >
            <div className="flex flex-col gap-6">
                <div className="grid grid-cols-2 gap-4">
                    <form.Field name="title">
                        {field => (
                            <AstraTextField
                                label="Tittel på aktivitet"
                                value={field.state.value}
                                placeholder="Aktivitet"
                                fullWidth
                                onChange={event => {
                                    const newTitle = event.target.value
                                    field.handleChange(newTitle)

                                    // Auto-update label with the first letter of the title
                                    field.form.setFieldValue('label', newTitle.charAt(0).toUpperCase())

                                    // Auto-suggest keyboard shortcut when creating a new status
                                    if (!statusDefinition && !field.form.state.fieldMeta.keyboardShortcut?.isTouched) {
                                        const suggestion = suggestShortcut(newTitle, existingShortcuts)
                                        if (suggestion) applyShortcutToForm(suggestion)
                                    }
                                }}
                                {...createFormFieldMetaProps(field)}
                            />
                        )}
                    </form.Field>

                    <form.Field name="label">
                        {field => (
                            <AstraTextField
                                label="Forkortelse"
                                value={field.state.value}
                                placeholder="A"
                                fullWidth
                                onChange={event => field.handleChange(event.target.value)}
                                {...createFormFieldMetaProps(field)}
                            />
                        )}
                    </form.Field>

                    <form.Field name="timeOfDay">
                        {field => (
                            <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                    <AstraParagraph variant="body-small" weight="medium">
                                        Tid på dagen
                                    </AstraParagraph>
                                </div>

                                <AstraSelect
                                    options={timeOfDayOptions}
                                    value={field.state.value}
                                    onChange={value => field.handleChange(value)}
                                    fullWidth
                                    isClearable
                                    isDisabled={statusDefinition?.standard}
                                    helperText={statusDefinition?.standard ? 'Kan ikke endres for standardaktiviteter' : undefined}
                                    {...createFormFieldMetaProps(field)}
                                />
                            </div>
                        )}
                    </form.Field>

                    <form.Field name="departments">
                        {field => (
                            <AstraInputWrapper label="Avdeling" borderless className={astraTwMerge('flex-1 p-0', statusDefinition?.standard && 'mt-5')}>
                                <AstraMultiSelect
                                    displayMode="inline"
                                    fullWidth
                                    options={departmentOptions}
                                    values={field.state.value}
                                    onChange={values => field.handleChange(values.map(value => value.value))}
                                />
                            </AstraInputWrapper>
                        )}
                    </form.Field>
                </div>

                <form.Field name="color">
                    {field => <ColorPicker colors={colorPalette} selectedColor={field.state.value} onColorChange={color => field.handleChange(color)} />}
                </form.Field>

                <form.Subscribe
                    selector={state => ({
                        title: state.values.title,
                        keyboardShortcut: state.values.keyboardShortcut,
                        keyboardHasShift: state.values.keyboardHasShift,
                        keyboardHasMeta: state.values.keyboardHasMeta,
                        shortcutError: state.fieldMeta.keyboardShortcut?.errors,
                        shortcutTouched: state.fieldMeta.keyboardShortcut?.isTouched,
                        shortcutValid: state.fieldMeta.keyboardShortcut?.isValid,
                    })}
                >
                    {({ title, keyboardShortcut, keyboardHasShift, keyboardHasMeta, shortcutTouched, shortcutValid, shortcutError }) => {
                        const shortcut = toKeyboardShortcut(keyboardShortcut, keyboardHasShift, keyboardHasMeta)
                        const currentShortcut = keyboardShortcut ? shortcut : undefined
                        const suggestion = suggestShortcut(title, existingShortcuts, currentShortcut)
                        const showError = shortcutTouched && !shortcutValid
                        const firstError = shortcutError?.[0]?.message

                        return (
                            <KeyboardShortcutField
                                shortcut={shortcut}
                                onShortcutChange={applyShortcutToForm}
                                error={showError}
                                errorText={showError ? firstError : undefined}
                                suggestion={suggestion}
                                canSuggest={title.trim().length > 0}
                                onSuggest={() => {
                                    if (suggestion) applyShortcutToForm(suggestion)
                                }}
                            />
                        )
                    }}
                </form.Subscribe>

                <form.Subscribe selector={state => state.values}>
                    {values => (
                        <StatusPreview
                            title={values.title}
                            label={values.label}
                            color={values.color}
                            shortcut={toKeyboardShortcut(values.keyboardShortcut, values.keyboardHasShift, values.keyboardHasMeta)}
                        />
                    )}
                </form.Subscribe>
            </div>
        </AstraDialog>
    )
}
